import { useEffect, useId, useRef, useState } from 'react';
import { ApiError } from '../../identity/types';
import { ErrorSummary, type SummaryIssue } from '../../job-roles/components/ErrorSummary';
import { ModalDialog } from '../../job-roles/components/ModalDialog';
import { healthcareErrorMessage } from '../healthcareErrors';
import { healthcareRatesGateway } from '../index';
import { HEALTHCARE_TERM_HINTS, HdcErrorCode, payBasisLabels, type PayBasis, type ServiceType } from '../types';
import { useSupportedPayBases } from '../useSupportedPayBases';
import '../../job-roles/job-roles.css';
import '../healthcare-rates.css';

export interface ServiceTypeDialogProps {
  /** The company's own work type being edited; omit to add a new one. System rows are never edited (the server refuses it). */
  editing?: ServiceType | undefined;
  /** The industry tags a new work type carries by default (the company's primary industry's sector). Used only to suggest it first. */
  defaultTags: string[];
  /** The company's primary industry in words, shown so the person can see what the work type is suggested for. Null when none is chosen. */
  industryLabel: string | null;
  /** True for a healthcare company: the dialog says that a work type may be a service or visit type there. */
  healthcare?: boolean | undefined;
  onClose: () => void;
  onSaved: (serviceType: ServiceType) => void;
}

type Field = 'name' | 'code';

const NAME_MAX = 100;
const CODE_MAX = 20;
const DESCRIPTION_MAX = 500;

/**
 * Add or edit a company work type: the WORK PERFORMED (a service call, a night shift, a delivery, a project, a class, a patient visit), never a worker
 * category. Duplicate names and codes are shown on the field they are about, a stale edit (409 CONCURRENCY_CONFLICT) says to reload, and nothing typed is
 * lost on a failure. The suggested pay basis is a hint only: it is never applied to a rate or a line for the person. A company admin does not pick
 * industries here: a new work type is suggested for the company's own industry (the full industry list is for EPN's own templates).
 */
export function ServiceTypeDialog({ editing, defaultTags, industryLabel, healthcare = false, onClose, onSaved }: ServiceTypeDialogProps) {
  const payBases = useSupportedPayBases();
  const uid = useId();
  const formId = `${uid}-form`;
  const [name, setName] = useState(editing?.name ?? '');
  const [code, setCode] = useState(editing?.code ?? '');
  const [description, setDescription] = useState(editing?.description ?? '');
  const [basis, setBasis] = useState<PayBasis | ''>(editing?.suggestedPayBasis ?? '');
  // A saved suggestion stays visible (and so the shown value matches the submitted one) even when the supported list cannot currently offer it for a new choice.
  const basisOptions = basis !== '' && !payBases.includes(basis) ? [...payBases, basis] : payBases;
  const [isActive, setIsActive] = useState(true);
  const [errors, setErrors] = useState<Partial<Record<Field, string>>>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [focusNonce, setFocusNonce] = useState(0);
  const summaryRef = useRef<HTMLDivElement>(null);
  const nameRef = useRef<HTMLInputElement>(null);
  const codeRef = useRef<HTMLInputElement>(null);
  const title = editing ? 'Edit work type' : 'Add a Work Type';

  useEffect(() => {
    if (focusNonce > 0) summaryRef.current?.focus();
  }, [focusNonce]);

  const issues: SummaryIssue[] = (['name', 'code'] as Field[]).filter((f) => errors[f]).map((f) => ({ key: f, message: errors[f]! }));
  const clearError = (key: Field) => setErrors((prev) => { const next = { ...prev }; delete next[key]; return next; });
  const focusField = (key: string) => (key === 'code' ? codeRef : nameRef).current?.focus();
  const describe = (key: Field, hint?: string) => [errors[key] ? `${uid}-${key}-error` : null, hint].filter(Boolean).join(' ') || undefined;

  async function submit() {
    const cleanName = name.replace(/\s+/g, ' ').trim();
    const cleanCode = code.trim().toUpperCase();
    const found: Partial<Record<Field, string>> = {};
    if (!cleanName) found.name = 'Enter a work type name.';
    else if (cleanName.length > NAME_MAX) found.name = `The name must be ${NAME_MAX} characters or fewer.`;
    if (!cleanCode) found.code = 'Enter a short code, like SVC-CALL.';
    else if (cleanCode.length > CODE_MAX) found.code = `The short code must be ${CODE_MAX} characters or fewer.`;
    setErrors(found);
    setFormError(null);
    if (Object.keys(found).length > 0) {
      setFocusNonce((n) => n + 1);
      return;
    }
    setBusy(true);
    try {
      const input = {
        name: cleanName, code: cleanCode, description: description.trim(), industries: editing ? editing.industries : defaultTags,
        suggestedPayBasis: basis === '' ? null : basis, isActive: editing ? editing.isActive : isActive,
      };
      const saved = editing
        ? await healthcareRatesGateway.updateServiceType(editing.id, input, editing.concurrencyVersion)
        : await healthcareRatesGateway.createServiceType(input);
      onSaved(saved);
    } catch (err) {
      setBusy(false);
      if (err instanceof ApiError && err.status === 409 && err.code === HdcErrorCode.ServiceTypeNameExists) {
        setErrors({ name: 'A work type with this name already exists. Choose a different name.' });
        setFocusNonce((n) => n + 1);
      } else if (err instanceof ApiError && err.status === 409 && err.code === HdcErrorCode.ServiceTypeCodeExists) {
        setErrors({ code: 'This short code is already used by another work type. Choose a different code.' });
        setFocusNonce((n) => n + 1);
      } else {
        setFormError(healthcareErrorMessage(err, 'The work type could not be saved.'));
      }
    }
  }

  return (
    <ModalDialog
      title={title}
      size="md"
      busy={busy}
      onClose={onClose}
      footer={(
        <>
          <button type="button" className="jr-btn jr-btn--secondary" onClick={onClose} disabled={busy}>Cancel</button>
          <button type="submit" form={formId} className="jr-btn jr-btn--primary" disabled={busy}>{busy ? 'Saving…' : editing ? 'Save changes' : 'Add work type'}</button>
        </>
      )}
    >
      <form id={formId} noValidate onSubmit={(e) => { e.preventDefault(); void submit(); }}>
        <p className="jr-hint hdc-intro">
          A work type describes the work performed, such as a service call, night shift, delivery, project, class or patient visit. It is a company setting and does not
          determine employee pay until connected to a compensation rate.
          {healthcare ? ` ${HEALTHCARE_TERM_HINTS.workType}` : ''}
        </p>
        <ErrorSummary ref={summaryRef} issues={issues} onSelect={focusField} />
        <div className="jr-grid">
          <div className="jr-field">
            <label htmlFor={`${uid}-name`}>Name <span aria-hidden="true" className="jr-req">*</span></label>
            <input
              ref={nameRef} id={`${uid}-name`} data-autofocus value={name} maxLength={NAME_MAX} autoComplete="off" aria-required="true"
              aria-invalid={errors.name ? true : undefined} aria-describedby={describe('name')}
              onChange={(e) => { setName(e.target.value); clearError('name'); }}
            />
            {errors.name && <p id={`${uid}-name-error`} className="jr-error" role="alert">{errors.name}</p>}
          </div>
          <div className="jr-field">
            <label htmlFor={`${uid}-code`}>Short code <span aria-hidden="true" className="jr-req">*</span></label>
            <input
              ref={codeRef} id={`${uid}-code`} value={code} maxLength={CODE_MAX} autoComplete="off" aria-required="true" className="hdc-upper"
              aria-invalid={errors.code ? true : undefined} aria-describedby={describe('code', `${uid}-code-hint`)}
              onChange={(e) => { setCode(e.target.value.toUpperCase()); clearError('code'); }}
            />
            <p id={`${uid}-code-hint`} className="jr-hint">Up to {CODE_MAX} characters. It is shown in upper case.</p>
            {errors.code && <p id={`${uid}-code-error`} className="jr-error" role="alert">{errors.code}</p>}
          </div>
          <div className="jr-field jr-grid__wide">
            <label htmlFor={`${uid}-description`}>Description (optional)</label>
            <textarea id={`${uid}-description`} rows={2} value={description} maxLength={DESCRIPTION_MAX} aria-describedby={`${uid}-description-hint`} onChange={(e) => setDescription(e.target.value)} />
            <p id={`${uid}-description-hint`} className="jr-hint">{description.length} of {DESCRIPTION_MAX} characters.</p>
          </div>
          <div className="jr-field">
            <label htmlFor={`${uid}-basis`}>Suggested pay basis (optional)</label>
            <select id={`${uid}-basis`} value={basis} aria-describedby={`${uid}-basis-hint`} onChange={(e) => setBasis(e.target.value as PayBasis | '')}>
              <option value="">No suggestion</option>
              {basisOptions.map((b) => <option key={b} value={b}>{payBasisLabels[b]}</option>)}
            </select>
            <p id={`${uid}-basis-hint`} className="jr-hint">A hint only. It never changes a rate or a pay-run line for you.</p>
          </div>
          {!editing && (
            <div className="jr-field">
              <span className="jr-fieldhead__label">Status</span>
              <label className="hdc-check">
                <input type="checkbox" checked={isActive} onChange={(e) => setIsActive(e.target.checked)} />
                <span>Active (offered when adding rates and lines)</span>
              </label>
            </div>
          )}
          <div className="jr-field jr-grid__wide">
            <span className="jr-fieldhead__label">Suggested for</span>
            <p data-testid="work-type-industry">{editing ? (editing.industries.length === 0 ? 'Any industry' : 'The industries it already has') : (industryLabel ?? 'Your company (no industry chosen)')}</p>
            <p className="jr-hint">Only used to list it first for your industry. You can use it for any rate or pay-run line.</p>
          </div>
        </div>
        {formError && <p className="jr-error jr-error--block" role="alert">{formError}</p>}
      </form>
    </ModalDialog>
  );
}
